import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useGasI18n } from '../../shared/kit';
import { followTopic, unfollowTopic } from './api';
import type { TopicFollowChannel, TopicFollowInitData } from './types';

interface AppProps {
    initData: TopicFollowInitData;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function sameChannels(a: TopicFollowChannel[], b: TopicFollowChannel[]): boolean {
    if (a.length !== b.length) {
        return false;
    }
    const sa = [...a].sort();
    const sb = [...b].sort();
    return sa.every((v, i) => v === sb[i]);
}

/**
 * Île React « Suivre cette rubrique » (archive éditoriale de catégorie).
 *
 * Rendue par GasTopicFollowWidgetRenderer::render(). Backend :
 * GasTopicFollowService + REST gas/v1/topic-follows (follow/unfollow/status).
 * Décalque très direct de resources/react/frontend/entity-follow/App.tsx,
 * avec un ajout : le choix des canaux (e-mail et/ou push web) — le push
 * n'existe que pour un connecté (pas de device lié pour un invité).
 */
export default function App({ initData }: AppProps) {
    const { t, tn } = useGasI18n();

    const storageKey = useMemo(() => `gas-topic-follow-${initData.topic_id}`, [initData.topic_id]);

    /**
     * Mémoire invité : { email, confirmed }. Le serveur ne révèle jamais
     * l'état de suivi d'un e-mail tiers (`status` ne prend pas d'e-mail en
     * entrée) — seul le localStorage permet de restaurer l'état affiché.
     */
    const storedGuest = useMemo(() => {
        if (initData.is_logged_in) {
            return { email: '', confirmed: false };
        }
        try {
            const raw = window.localStorage.getItem(storageKey) || '';
            if (raw === '') {
                return { email: '', confirmed: false };
            }
            const parsed = JSON.parse(raw) as { email?: string; confirmed?: boolean };
            return { email: String(parsed.email || ''), confirmed: Boolean(parsed.confirmed) };
        } catch {
            return { email: '', confirmed: false };
        }
    }, [initData.is_logged_in, storageKey]);

    const storedGuestEmail = storedGuest.email;

    const [following, setFollowing] = useState<boolean>(
        initData.is_logged_in ? initData.following : storedGuest.confirmed,
    );
    const [pending, setPending] = useState<boolean>(
        !initData.is_logged_in && storedGuest.email !== '' && !storedGuest.confirmed,
    );
    const [followersCount, setFollowersCount] = useState<number>(initData.followers);
    const [channels, setChannels] = useState<TopicFollowChannel[]>(
        initData.channels.length > 0 ? initData.channels : ['email'],
    );
    const [savedChannels, setSavedChannels] = useState<TopicFollowChannel[]>(channels);
    const [showGuestForm, setShowGuestForm] = useState(false);
    const [guestEmail, setGuestEmail] = useState(storedGuestEmail);
    const [guestName, setGuestName] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [info, setInfo] = useState<string | null>(null);

    const rememberGuest = useCallback(
        (email: string, confirmed = false) => {
            try {
                if (email) {
                    window.localStorage.setItem(storageKey, JSON.stringify({ email, confirmed }));
                } else {
                    window.localStorage.removeItem(storageKey);
                }
            } catch {
                // localStorage indisponible (navigation privée…) : état non persisté.
            }
        },
        [storageKey],
    );

    // Retour depuis le lien de confirmation e-mail
    // (?gas_topic_follow_msg=confirmed|unsubscribed|error).
    useEffect(() => {
        let msg: string | null = null;
        try {
            msg = new URLSearchParams(window.location.search).get('gas_topic_follow_msg');
        } catch {
            msg = null;
        }
        if (msg === 'confirmed') {
            setInfo(t('topic_follow.confirmed'));
            setPending(false);
            setFollowing(true);
            if (storedGuestEmail !== '') {
                rememberGuest(storedGuestEmail, true);
            }
        } else if (msg === 'unsubscribed') {
            setInfo(t('topic_follow.unfollowed'));
            setFollowing(false);
            setPending(false);
            rememberGuest('');
        } else if (msg === 'error') {
            setError(t('topic_follow.error_invalid_token'));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggleChannel = useCallback((channel: TopicFollowChannel) => {
        setChannels((prev) => {
            if (prev.includes(channel)) {
                const next = prev.filter((c) => c !== channel);
                return next.length > 0 ? next : prev; // au moins un canal reste sélectionné
            }
            return [...prev, channel];
        });
    }, []);

    const handleFollow = useCallback(
        async (guest?: { email: string; name?: string }) => {
            setBusy(true);
            setError(null);
            setInfo(null);
            try {
                const requestedChannels = guest ? channels.filter((c) => c === 'email') : channels;
                const result = await followTopic(
                    initData.config,
                    initData.topic_id,
                    requestedChannels.length > 0 ? requestedChannels : ['email'],
                    guest,
                );
                setFollowersCount(result.followers);
                if (guest) {
                    setShowGuestForm(false);
                    if (result.needs_confirmation) {
                        // PAS « Suivi » : tant que le lien n'est pas cliqué,
                        // cette adresse ne recevra aucune alerte.
                        rememberGuest(guest.email, false);
                        setPending(true);
                        setFollowing(false);
                        setInfo(null);
                    } else {
                        rememberGuest(guest.email, true);
                        setPending(false);
                        setFollowing(result.following);
                    }
                } else {
                    setFollowing(result.following);
                }
                setSavedChannels(result.channels.length > 0 ? result.channels : requestedChannels);
            } catch (e) {
                setError(e instanceof Error && e.message ? e.message : t('topic_follow.error_failed'));
            } finally {
                setBusy(false);
            }
        },
        [initData, channels, rememberGuest, t],
    );

    const handleUnfollow = useCallback(async () => {
        setBusy(true);
        setError(null);
        try {
            const result = await unfollowTopic(
                initData.config,
                initData.topic_id,
                initData.is_logged_in ? undefined : guestEmail,
            );
            setFollowing(false);
            setPending(false);
            setFollowersCount(result.followers);
            setInfo(null);
            rememberGuest('');
        } catch (e) {
            setError(e instanceof Error && e.message ? e.message : t('topic_follow.error_failed'));
        } finally {
            setBusy(false);
        }
    }, [initData, guestEmail, rememberGuest, t]);

    const handleGuestSubmit = useCallback(
        (event: React.FormEvent) => {
            event.preventDefault();
            const email = guestEmail.trim();
            if (!email || !EMAIL_RE.test(email)) {
                setError(t('topic_follow.error_invalid_recipient'));
                return;
            }
            void handleFollow({ email, name: guestName.trim() });
        },
        [guestEmail, guestName, handleFollow, t],
    );

    const channelsChanged = following && !sameChannels(channels, savedChannels);

    const renderChannelPicker = (guestMode: boolean) => (
        <fieldset className="gas-topic-follow__channels">
            <legend className="gas-topic-follow__heading">{t('topic_follow.channels_label')}</legend>
            <label className="gas-topic-follow__channel">
                <input
                    type="checkbox"
                    checked={channels.includes('email')}
                    onChange={() => toggleChannel('email')}
                />
                <span>{t('topic_follow.channel_email')}</span>
            </label>
            {!guestMode && (
                <label className="gas-topic-follow__channel">
                    <input
                        type="checkbox"
                        checked={channels.includes('webpush')}
                        onChange={() => toggleChannel('webpush')}
                    />
                    <span>{t('topic_follow.channel_webpush')}</span>
                </label>
            )}
            <p className="gas-topic-follow__channels-hint">{t('topic_follow.channels_hint')}</p>
        </fieldset>
    );

    return (
        <div className="gas-topic-follow__inner">
            {following ? (
                <div className="gas-topic-follow__followed" role="status">
                    <p className="gas-topic-follow__title">
                        <i className="fas fa-check-circle" aria-hidden="true"></i>{' '}
                        {t('topic_follow.button_following')}
                    </p>
                    {initData.is_logged_in && renderChannelPicker(false)}
                    <div className="gas-topic-follow__actions">
                        {channelsChanged && (
                            <button
                                type="button"
                                className="gas-button gas-button--secondary gas-button--xs"
                                onClick={() => void handleFollow()}
                                disabled={busy}
                            >
                                {t('common.save')}
                            </button>
                        )}
                        <button
                            type="button"
                            className="gas-button gas-button--outline gas-topic-follow__unfollow"
                            onClick={() => void handleUnfollow()}
                            disabled={busy}
                            aria-pressed="true"
                        >
                            {t('topic_follow.button_unfollow')}
                        </button>
                    </div>
                </div>
            ) : pending ? (
                <div className="gas-topic-follow__pending" role="status">
                    <p className="gas-topic-follow__hint">
                        <i className="fas fa-envelope" aria-hidden="true"></i>{' '}
                        {t('topic_follow.guest_pending')}
                    </p>
                    <button
                        type="button"
                        className="gas-button gas-button--outline gas-button--xs gas-topic-follow__resend"
                        onClick={() => void handleFollow({ email: storedGuestEmail })}
                        disabled={busy || storedGuestEmail === ''}
                    >
                        {t('topic_follow.guest_resend')}
                    </button>
                </div>
            ) : showGuestForm ? (
                <form className="gas-topic-follow__form" onSubmit={handleGuestSubmit}>
                    <p className="gas-topic-follow__hint">{t('topic_follow.guest_intro')}</p>
                    {renderChannelPicker(true)}
                    <div className="gas-topic-follow__fields">
                        <label className="gas-topic-follow__field">
                            <span>{t('topic_follow.guest_name_label')}</span>
                            <input
                                type="text"
                                value={guestName}
                                onChange={(e) => setGuestName(e.target.value)}
                                autoComplete="name"
                                aria-label={t('topic_follow.guest_name_label')}
                            />
                        </label>
                        <label className="gas-topic-follow__field">
                            <span>{t('topic_follow.guest_email_placeholder')}</span>
                            <input
                                type="email"
                                value={guestEmail}
                                onChange={(e) => setGuestEmail(e.target.value)}
                                placeholder={t('topic_follow.guest_email_placeholder')}
                                required
                                autoComplete="email"
                                aria-label={t('topic_follow.guest_email_placeholder')}
                            />
                        </label>
                    </div>
                    <div className="gas-topic-follow__actions">
                        <button type="submit" className="gas-button gas-button--primary" disabled={busy}>
                            {t('topic_follow.guest_submit')}
                        </button>
                        <button
                            type="button"
                            className="gas-button gas-button--outline"
                            onClick={() => {
                                setShowGuestForm(false);
                                setError(null);
                            }}
                            disabled={busy}
                        >
                            {t('common.cancel')}
                        </button>
                    </div>
                </form>
            ) : (
                <div className="gas-topic-follow__cta">
                    {initData.is_logged_in && renderChannelPicker(false)}
                    <button
                        type="button"
                        className="gas-button gas-button--primary"
                        onClick={() => {
                            if (initData.is_logged_in) {
                                void handleFollow();
                            } else {
                                setShowGuestForm(true);
                                setError(null);
                                setInfo(null);
                            }
                        }}
                        disabled={busy}
                        aria-pressed="false"
                    >
                        <i className="fas fa-bell" aria-hidden="true"></i>{' '}
                        {t('topic_follow.button_follow')}
                    </button>
                </div>
            )}

            {followersCount > 0 && (
                <p className="gas-topic-follow__count">
                    {tn('topic_follow.followers_count', followersCount)}
                </p>
            )}
            {error && (
                <p className="gas-topic-follow__error" role="alert">
                    {error}
                </p>
            )}
            {info && !error && <p className="gas-topic-follow__info">{info}</p>}
        </div>
    );
}
